import { useFocusEffect } from '@react-navigation/native';
import { setBadgeCountAsync } from 'expo-notifications';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { MeineRegionRouting } from '@/features/geo-area/navigation/MeineRegionRouting';
import { MeinOrtRouting } from '@/features/mein-ort/navigation/MeinOrtRouting';
import { ProfileSettingScreen } from '@/features/profile-setting/screens/ProfileSettingScreen';
import { StartScreen } from '@/features/start/screens/StartScreen';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { NavigationBar } from '@/navigation/components/navbar/NavigationBar';
import { BottomTabbar } from '@/navigation/components/tab/BottomTabbar';
import { renderTabIcon } from '@/navigation/components/tab/tabIcons';
import { TabStack } from '@/navigation/stacks/appStack';
import { TabRoute } from '@/navigation/types/navigation';
import { useBadgeStore, BadgeKey } from '@/shared/store/useBadgeStore';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

export const TabsRouting: FC = () => {
  const { colors } = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.NAVIGATION, {
    keyPrefix: 'tabs'
  });
  const badges = useBadgeStore((state) => state.badges);
  const updateBadge = useBadgeStore((state) => state.updateBadge);

  const unreadMessageCount = useMemo(
    () => badges[BadgeKey.Chat]?.value,
    [badges]
  );

  useEffect(() => {
    updateBadge(TabRoute.Profile, unreadMessageCount, colors.error);
  }, [colors.error, unreadMessageCount, updateBadge]);

  // reset app badge
  useFocusEffect(
    useCallback(() => {
      setBadgeCountAsync(0);
    }, [])
  );

  return (
    <TabStack.Navigator
      initialRouteName={TabRoute.Start}
      screenOptions={{
        headerShown: false,
        header: ({ options }) => (
          <NavigationBar
            hasBackButton={false}
            title={options.title}
            centerTitle
          />
        )
      }}
      tabBar={(props) => <BottomTabbar {...props} />}
    >
      <TabStack.Screen
        name={TabRoute.Start}
        component={StartScreen}
        options={{
          tabBarBadge: badges[TabRoute.Start]?.value,
          tabBarBadgeStyle: {
            backgroundColor: badges[TabRoute.Start]?.color
          },
          tabBarLabel: t(TabRoute.Start),
          tabBarIcon: (props) => renderTabIcon(TabRoute.Start, props)
        }}
      />
      <TabStack.Screen
        name={TabRoute.Radio}
        component={MeinOrtRouting}
        options={{
          headerShown: false,
          title: t(TabRoute.Radio),
          tabBarBadge: badges[TabRoute.Radio]?.value,
          tabBarBadgeStyle: {
            backgroundColor: badges[TabRoute.Radio]?.color
          },
          tabBarLabel: t(TabRoute.Radio),
          tabBarIcon: (props) => renderTabIcon(TabRoute.Radio, props)
        }}
      />
      <TabStack.Screen
        name={TabRoute.Region}
        component={MeineRegionRouting}
        options={{
          headerShown: false,
          title: t(TabRoute.Region),
          tabBarBadge: badges[TabRoute.Region]?.value,
          tabBarBadgeStyle: {
            backgroundColor: badges[TabRoute.Region]?.color
          },
          tabBarLabel: t(TabRoute.Region),
          tabBarIcon: (props) => renderTabIcon(TabRoute.Region, props)
        }}
      />
      <TabStack.Screen
        name={TabRoute.Profile}
        component={ProfileSettingScreen}
        options={{
          headerShown: true,
          title: t(TabRoute.Profile),
          tabBarBadge: badges[TabRoute.Profile]?.value,
          tabBarBadgeStyle: {
            backgroundColor: badges[TabRoute.Profile]?.color
          },
          tabBarLabel: t(TabRoute.Profile),
          tabBarIcon: (props) => renderTabIcon(TabRoute.Profile, props)
        }}
      />
    </TabStack.Navigator>
  );
};
